// AddViewSupportCertComponent.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Label, Table, Input,
  Modal, ModalHeader, ModalBody, Badge
} from "reactstrap";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { BsPlusCircle } from "react-icons/bs";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import { format, parseISO } from "date-fns";

// ---------- Thai/Buddhist locale ----------
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

// ---------- helpers ----------
const thaiMonths = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function formatMonthYearBE(date) {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
}
function toThaiDateLabel(iso) {
  if (!iso) return "-";
  const d = typeof iso === "string" ? parseISO(iso) : iso;
  const be = addYears(543, d);
  return `${format(be, "dd")} ${format(be, "MMMM", { locale: th })} ${format(be, "yyyy")}`;
}
function parseYearMonth(yyyymm) {
  const [y, m] = (yyyymm || "").split("-").map(Number);
  return { y, m };
}
function sameMonth(iso, y, m) {
  const d = typeof iso === "string" ? parseISO(iso) : iso;
  return d.getFullYear() === y && (d.getMonth() + 1) === m;
}

// ===============================================================
//                        MAIN COMPONENT
// ===============================================================
export function AddViewSupport410Component() {
  const [reportMonth, setReportMonth] = useState("");     // "YYYY-MM"
  const [reportMonthDate, setReportMonthDate] = useState(null);
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const deptId = user?.deptId || "";

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isEditingReport, setIsEditingReport] = useState(false);

  // master: สาขาอาชีพ (เพื่อแสดงชื่อ)
  const [lawOcuData, setLawOcuData] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/Borrow/getLawOcuData");
        const j = await res.json();
        setLawOcuData(j?.dataenum || []);
      } catch { }
    })();
  }, []);
  const getLawOccName = (id) =>
    lawOcuData.find(x => String(x.lawOccupationId) === String(id))?.lawOccupationName || "-";

  // sync string <-> Date
  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  // summary
  const summary = useMemo(() => {
    const tutor = items.reduce((a, r) => a + Number(r.tutorAmountExpense || 0), 0);
    const lunch = items.reduce((a, r) => a + Number(r.lunchAmountExpense || 0), 0);
    const bake = items.reduce((a, r) => a + Number(r.bakeAmountExpense || 0), 0);
    const util = items.reduce((a, r) => a + Number(r.utilityAmountExpense || 0), 0);
    const total = tutor + lunch + bake + util;
    return { tutor, lunch, bake, util, total };
  }, [items]);

  // Load month
  const checkAndLoadMonth = async () => {
    if (!reportMonth || !deptId) {
      Swal.fire("กรุณาเลือกรอบรายงาน (เดือน/ปี)", "", "warning");
      return;
    }
    try {
      setLoading(true);
      const q = new URLSearchParams({ monthYear: reportMonth, deptId });
      const res = await fetch(`/api/Support/viewCert?${q.toString()}`);
      const j = await res.json();
      if (j?.status !== 1) throw new Error(j?.message || "โหลดข้อมูลไม่ได้");

      const rows = (j?.dataenum || []).map((s, i) => ({
        id: s.supportOperationCertId ?? i + 1,
        deptId: s.deptId,
        lawOccupationId: s.lawOccupationId,
        courseName: s.courseName || "-",
        testingPersonAmount: Number(s.testingPersonAmount || 0),
        passPersonAmount: Number(s.passPersonAmount || 0),
        tutorAmountExpense: Number(s.tutorAmountExpense || 0),
        lunchAmountExpense: Number(s.lunchAmountExpense || 0),
        bakeAmountExpense: Number(s.bakeAmountExpense || 0),
        utilityAmountExpense: Number(s.utilityAmountExpense || 0),
        paymentDate: s.paymentDate, // yyyy-MM-dd
      }));

      setItems(rows);
      setIsEditingReport(rows.length > 0);
    } catch (e) {
      console.error(e);
      Swal.fire("โหลดข้อมูลเดือนไม่สำเร็จ", e?.message || "", "error");
      setItems([]);
      setIsEditingReport(false);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => { setEditingItem(null); setOpenModal(true); };
  const openEdit = (row) => { setEditingItem(row); setOpenModal(true); };
  const closeModal = () => setOpenModal(false);

  return (
    <Container className="py-3">
      {/* Header controls */}
      <Row className="g-3 align-items-end">
        <Col md={5}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`);
                setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else {
                setReportMonth(""); setReportMonthDate(null);
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 120 }}
                    value={thaiMonths[getMonth(date)]}
                    onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}
                  >
                    {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 100 }}
                    value={getYear(date)}
                    onChange={({ target: { value } }) => changeYear(Number(value))}
                  >
                    {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={
              <input className="form-control rounded-pill text-center input-color" readOnly
                value={formatMonthYearBE(reportMonthDate)} placeholder="เลือกเดือน/ปี (พ.ศ.)" />
            }
          />
        </Col>

        <Col md="auto" className="pt-4">
          <button className="btn btn-primary" onClick={checkAndLoadMonth} disabled={!reportMonth}>
            ตรวจสอบ/โหลดรายงาน
          </button>
        </Col>

        <Col md="auto" className="pt-4">
          <button className="btn btn-success" onClick={openAdd} disabled={!reportMonth}>
            <BsPlusCircle className="me-1" /> เพิ่มรายการ
          </button>
        </Col>

        <Col md="auto" className="pt-4">
          {reportMonth && (
            <Badge color={isEditingReport ? "warning" : "success"} pill className="px-3 py-2">
              {isEditingReport ? "โหมดแก้ไขรายงาน" : "โหมดสร้างรายงานใหม่"}
            </Badge>
          )}
        </Col>
      </Row>

      {/* Table */}
      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan="2" className="text-center align-middle">ที่</th>
              <th rowSpan="2" className="text-center align-middle">สาขาอาชีพ</th>
              <th rowSpan="2" className="text-center align-middle">หลักสูตร</th>
              <th rowSpan="2" className="text-center align-middle">จำนวนผู้เข้ารับการฝึกอบรมฯ<br />(คน)</th>
              <th rowSpan="2" className="text-center align-middle">จำนวนผู้ผ่านการฝึกอบรมฯ<br />(คน)</th>
              <th colSpan="4" className="text-center align-middle">จำนวนเงินช่วยเหลือหรืออุดหนุน</th>
              <th rowSpan="2" className="text-center align-middle">รวม<br />(บาท)</th>
              <th rowSpan="2" className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
              <th rowSpan="2" className="text-center align-middle">จัดการ</th>
            </tr>
            <tr>
              <th className="text-center align-middle">ค่าตอบแทนวิทยากร<br />(บาท)</th>
              <th className="text-center align-middle">ค่าอาหารกลางวัน<br />(บาท)</th>
              <th className="text-center align-middle">ค่าอาหารว่าง และเครื่องดื่ม<br />(บาท)</th>
              <th className="text-center align-middle">ค่าวัสดุ<br />(บาท)</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={12} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {items.map((r, idx) => {
              const total = Number(r.tutorAmountExpense || 0) + Number(r.lunchAmountExpense || 0) +
                Number(r.bakeAmountExpense || 0) + Number(r.utilityAmountExpense || 0);
              return (
                <tr key={r.id || idx}>
                  <td className="text-center">{idx + 1}</td>
                  <td className="text-center">{getLawOccName(r.lawOccupationId)}</td>
                  <td>{r.courseName || "-"}</td>
                  <td className="text-center">{Number(r.testingPersonAmount || 0).toLocaleString()}</td>
                  <td className="text-center">{Number(r.passPersonAmount || 0).toLocaleString()}</td>
                  <td className="text-center">{fmtMoney(r.tutorAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(r.lunchAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(r.bakeAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(r.utilityAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(total)}</td>
                  <td className="text-center">{toThaiDateLabel(r.paymentDate)}</td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(r)}>แก้ไข</button>
                  </td>
                </tr>
              );
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={5} className="text-end fw-bold">รวม</td>
                <td className="text-center fw-bold">{fmtMoney(summary.tutor)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.lunch)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.bake)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.util)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.total)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {/* Modal */}
      <Modal isOpen={openModal} toggle={closeModal} centered size="lg" backdrop="static">
        <ModalHeader toggle={closeModal}>{editingItem ? "แก้ไขรายการ" : "เพิ่มรายการ"}</ModalHeader>
        <ModalBody>
          <CertForm
            deptId={deptId}
            reportMonth={reportMonth}
            editingItem={editingItem}
            lawOcuData={lawOcuData}
            onSaved={(saved) => {
              if (editingItem) {
                setItems(prev => prev.map(x => String(x.id) === String(saved.id) ? saved : x));
              } else {
                setItems(prev => [...prev, saved]);
              }
              closeModal();
            }}
          />
        </ModalBody>
      </Modal>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <span className="badge bg-secondary">กำลังโหลด...</span>
        </div>
      )}
    </Container>
  );
}

// ===============================================================
//                          FORM
// ===============================================================
function CertForm({ deptId, reportMonth, editingItem, lawOcuData, onSaved }) {
  const schema = Yup.object().shape({
    lawOccupationId: Yup.string().required("เลือกสาขาอาชีพ"),
    courseName: Yup.string().required("กรอกหลักสูตร"),
    testingPersonAmount: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    passPersonAmount: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    tutorAmountExpense: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    lunchAmountExpense: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    bakeAmountExpense: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    utilityAmountExpense: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    paymentDate: Yup.string().required("เลือกวันที่จ่าย"),
  });

  const init = {
    id: editingItem?.id ?? editingItem?.supportOperationCertId ?? null,
    deptId,
    lawOccupationId: editingItem?.lawOccupationId ?? "",
    courseName: editingItem?.courseName ?? "",
    testingPersonAmount: editingItem?.testingPersonAmount ?? 0,
    passPersonAmount: editingItem?.passPersonAmount ?? 0,
    tutorAmountExpense: editingItem?.tutorAmountExpense ?? 0,
    lunchAmountExpense: editingItem?.lunchAmountExpense ?? 0,
    bakeAmountExpense: editingItem?.bakeAmountExpense ?? 0,
    utilityAmountExpense: editingItem?.utilityAmountExpense ?? 0,
    paymentDate: editingItem?.paymentDate ?? "",
  };

  const { y, m } = parseYearMonth(reportMonth || "2000-01");

  return (
    <Formik
      initialValues={init}
      validationSchema={schema}
      onSubmit={async (v, act) => {
        try {
          if (!sameMonth(v.paymentDate, y, m)) {
            Swal.fire("วันที่จ่ายต้องอยู่ในเดือนที่เลือก", "", "error");
            return;
          }
          const payload = {
            supportOperationCertId: v.id,
            deptId: v.deptId,
            lawOccupationId: v.lawOccupationId,
            courseName: v.courseName,
            testingPersonAmount: Number(v.testingPersonAmount || 0),
            passPersonAmount: Number(v.passPersonAmount || 0),
            tutorAmountExpense: Number(v.tutorAmountExpense || 0),
            lunchAmountExpense: Number(v.lunchAmountExpense || 0),
            bakeAmountExpense: Number(v.bakeAmountExpense || 0),
            utilityAmountExpense: Number(v.utilityAmountExpense || 0),
            paymentDate: v.paymentDate,
          };

          const res = await fetch("/api/Support/saveCert", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const j = await res.json();

          if (j?.status === 1) {
            onSaved && onSaved({ ...v, id: v.id || j.id });
            Swal.fire("บันทึกสำเร็จ", "", "success");
          } else {
            Swal.fire("บันทึกไม่สำเร็จ", j?.message || "", "warning");
          }
        } catch (e) {
          Swal.fire("บันทึกไม่สำเร็จ", e?.message || "", "error");
        } finally {
          act.setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, touched, errors }) => {
        const selectedDate = values.paymentDate ? new Date(values.paymentDate) : null;
        const total =
          Number(values.tutorAmountExpense || 0) +
          Number(values.lunchAmountExpense || 0) +
          Number(values.bakeAmountExpense || 0) +
          Number(values.utilityAmountExpense || 0);

        return (
          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Label>สาขาอาชีพ</Label>
                <Field name="lawOccupationId">
                  {({ field }) => (
                    <Input
                      type="select"
                      {...field}
                      className={`rounded-pill text-center ${touched.lawOccupationId && errors.lawOccupationId ? "is-invalid" : ""}`}
                    >
                      <option value="">กรุณาเลือก</option>
                      {lawOcuData.map(l => (
                        <option key={l.lawOccupationId} value={l.lawOccupationId}>
                          {l.lawOccupationName}
                        </option>
                      ))}
                    </Input>
                  )}
                </Field>
                {touched.lawOccupationId && errors.lawOccupationId && (
                  <div className="invalid-feedback d-block">{errors.lawOccupationId}</div>
                )}
              </Col>

              <Col md={6}>
                <Label>หลักสูตร</Label>
                <Field name="courseName">
                  {({ field }) => (
                    <Input {...field} className={`rounded-pill ${touched.courseName && errors.courseName ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.courseName && errors.courseName && (
                  <div className="invalid-feedback d-block">{errors.courseName}</div>
                )}
              </Col>

              <Col md={6}>
                <Label>จำนวนผู้เข้ารับการฝึกอบรมฯ (คน)</Label>
                <Field name="testingPersonAmount">
                  {({ field }) => (
                    <Input type="number" min="0" {...field}
                      className={`rounded-pill text-center ${touched.testingPersonAmount && errors.testingPersonAmount ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.testingPersonAmount && errors.testingPersonAmount && (
                  <div className="invalid-feedback d-block">{errors.testingPersonAmount}</div>
                )}
              </Col>

              <Col md={6}>
                <Label>จำนวนผู้ผ่านการฝึกอบรมฯ (คน)</Label>
                <Field name="passPersonAmount">
                  {({ field }) => (
                    <Input type="number" min="0" {...field}
                      className={`rounded-pill text-center ${touched.passPersonAmount && errors.passPersonAmount ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.passPersonAmount && errors.passPersonAmount && (
                  <div className="invalid-feedback d-block">{errors.passPersonAmount}</div>
                )}
              </Col>

              <Col md={6}>
                <Label>ค่าตอบแทนวิทยากร (บาท)</Label>
                <Field name="tutorAmountExpense">
                  {({ field }) => (
                    <Input type="number" min="0" step="0.01" {...field}
                      className={`rounded-pill text-center ${touched.tutorAmountExpense && errors.tutorAmountExpense ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.tutorAmountExpense && errors.tutorAmountExpense && (
                  <div className="invalid-feedback d-block">{errors.tutorAmountExpense}</div>
                )}
              </Col>

              <Col md={6}>
                <Label>ค่าอาหารกลางวัน (บาท)</Label>
                <Field name="lunchAmountExpense">
                  {({ field }) => (
                    <Input type="number" min="0" step="0.01" {...field}
                      className={`rounded-pill text-center ${touched.lunchAmountExpense && errors.lunchAmountExpense ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.lunchAmountExpense && errors.lunchAmountExpense && (
                  <div className="invalid-feedback d-block">{errors.lunchAmountExpense}</div>
                )}
              </Col>

              <Col md={6}>
                <Label>ค่าอาหารว่าง และเครื่องดื่ม (บาท)</Label>
                <Field name="bakeAmountExpense">
                  {({ field }) => (
                    <Input type="number" min="0" step="0.01" {...field}
                      className={`rounded-pill text-center ${touched.bakeAmountExpense && errors.bakeAmountExpense ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.bakeAmountExpense && errors.bakeAmountExpense && (
                  <div className="invalid-feedback d-block">{errors.bakeAmountExpense}</div>
                )}
              </Col>

              <Col md={6}>
                <Label>ค่าวัสดุ (บาท)</Label>
                <Field name="utilityAmountExpense">
                  {({ field }) => (
                    <Input type="number" min="0" step="0.01" {...field}
                      className={`rounded-pill text-center ${touched.utilityAmountExpense && errors.utilityAmountExpense ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.utilityAmountExpense && errors.utilityAmountExpense && (
                  <div className="invalid-feedback d-block">{errors.utilityAmountExpense}</div>
                )}
              </Col>

              <Col md={6}>
                <Label>รวม (บาท)</Label>
                <Input readOnly className="rounded-pill text-center"
                  value={fmtMoney(total)} />
              </Col>

              <Col md={6}>
                <Label>วันที่จ่ายเงินช่วยเหลือ/อุดหนุน</Label>
                <DatePicker
                  selected={selectedDate}
                  locale="th-buddhist"
                  dateFormat="dd MMMM yyyy"
                  placeholderText="กรุณาเลือก"
                  renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                    <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                      <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                      <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <select className="form-select form-select-sm" style={{ width: 140 }}
                          value={thaiMonths[(selectedDate || date).getMonth()]}
                          onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}>
                          {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select className="form-select form-select-sm" style={{ width: 110 }}
                          value={(selectedDate || date).getFullYear()}
                          onChange={({ target: { value } }) => changeYear(Number(value))}>
                          {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                        </select>
                      </div>
                      <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
                    </div>
                  )}
                  onChange={(date) => {
                    if (date instanceof Date && !isNaN(date)) {
                      const y2 = date.getFullYear();
                      const m2 = String(date.getMonth() + 1).padStart(2, "0");
                      const d2 = String(date.getDate()).padStart(2, "0");
                      const iso = `${y2}-${m2}-${d2}`;
                      if (!sameMonth(iso, y, m)) {
                        Swal.fire("วันที่จ่ายต้องอยู่ในเดือนที่เลือก", "", "error"); return;
                      }
                      setFieldValue("paymentDate", iso);
                    } else {
                      setFieldValue("paymentDate", "");
                    }
                  }}
                  customInput={
                    <Input type="text" readOnly
                      value={values.paymentDate ? toThaiDateLabel(values.paymentDate) : ""}
                      className={`rounded-pill text-center ${touched.paymentDate && errors.paymentDate ? "is-invalid" : ""}`} />
                  }
                />
                {touched.paymentDate && errors.paymentDate && (
                  <div className="invalid-feedback d-block">{errors.paymentDate}</div>
                )}
              </Col>

              <div className="d-flex justify-content-between mt-3">
                <button type="button" className="btn btn-outline-secondary" onClick={() => window.history.back?.()}>ยกเลิก</button>
                <button type="submit" className="btn btn-success">{editingItem ? "บันทึกการแก้ไข" : "บันทึกรายการ"}</button>
              </div>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
}
